import PropTypes from 'prop-types';
import MessageBubble from './MessageBubble';
import { useRoomContext } from '../../../../../context/RoomContext';
import { formatFileSize } from '../../../../../utils/fileUtils';
import { getThumbnailBlobUrl } from '../../../../../hooks/useMediaThumbnail';
import { stripReplyFallback } from '../../../../../utils/replyUtils';
import './MessageGroup.css';

const MessageGroup = ({
  senderId = '',
  messages = [],
  name = '',
  avatar = '',
  shield = null,
  isUser = false,
  onDeleteMessage = (_messageId) => {},
  onReplyMessage = (_message, _senderName, _content) => {},
  onEditMessage = (_message, _content) => {},
  editHighlightedMessageId = null,
  selectedMessageId = null,
  allMessageGroups = [], // New prop to access all messages for reply lookup
  onReactionDetailsOpen = () => {},
  onMessageDetailsOpen = () => {},
  onFocusMessage = () => {},
  onContactClick = () => {},
  onEditHistoryOpen = () => {},
  retryingTransactionIds = null,
  pendingTransactionIds = null,
  unsentTransactionIds = null,
}) => {
  const { readReceipts } = useRoomContext();
  const isUserClass = (isUser) ? 'user' : '';

  const getReplyRelationContent = (message) => {
    let content = message.getClearContent?.() || message.getContent();
    // If m.relates_to is missing (public room edit), try to grab initial message before edits to find the reply relation
    if (!content['m.relates_to'] && message._isEdited) {
      const originalContent = message.getOriginalContent?.();
      if (originalContent && originalContent['m.relates_to']) {
        content = originalContent;
      }
    }
    return content;
  };

  const isReplyMessage = (message) => {
    const content = getReplyRelationContent(message);
    return content && content['m.relates_to'] && content['m.relates_to']['m.in_reply_to'];
  };

  const getOriginalReplyMessage = (replyMessage) => {
    const content = getReplyRelationContent(replyMessage);
    const replyToEventId = content['m.relates_to']?.['m.in_reply_to']?.event_id;
    if (!replyToEventId) return null;
    
    // Search through all message groups to find the original message
    for (const messageGroup of allMessageGroups) {
      for (const msg of messageGroup.messages) {
        if (msg.getId() === replyToEventId) {
          // Check if the original message was deleted
          if (msg.isRedacted && msg.isRedacted()) {
            return {
              eventId: replyToEventId,
              content: 'Message deleted',
              sender: msg.sender?.name || msg.getSender() || messageGroup.name,
              senderIsUser: messageGroup.isUser,
              isAttachment: false,
              thumbnailUrl: null,
              msgtype: null,
              filename: null,
              mimeType: null,
              size: null,
            };
          }

          // Check if the message has been edited - use the edited content if available
          // _editedContent is set by useRoom.js when processing edit events
          let originalMsgContent;
          if (msg._isEdited && msg._editedContent) {
            originalMsgContent = msg._editedContent;
          } else {
            originalMsgContent = msg.getClearContent?.() || msg.getContent();
          }
          
          const msgtype = originalMsgContent.msgtype;
          const isAttachment = ['m.image', 'm.video', 'm.audio', 'm.file'].includes(msgtype);
          const filename = isAttachment ? originalMsgContent.filename || originalMsgContent.body : null;
          const mimeType = isAttachment ? originalMsgContent.info?.mimetype : null;
          const size = isAttachment ? formatFileSize(originalMsgContent.info?.size) : null;
          // Extract caption for attachments (if body is different from filename)
          const caption = isAttachment && originalMsgContent.body !== filename 
            ? originalMsgContent.body 
            : null;
          
          // Strip reply fallback text to avoid showing Matrix IDs in nested replies
          const bodyText = stripReplyFallback(originalMsgContent.body) ?? originalMsgContent.body;
          
          // Use the pre-generated blob URL if it exists on the message object.
          const thumbnailUrl = getThumbnailBlobUrl(msg);
          return {
            eventId: replyToEventId,
            // For attachments, use filename as content, otherwise use cleaned body.
            content: isAttachment ? filename : bodyText,
            sender: msg.sender?.name || msg.getSender() || messageGroup.name,
            senderIsUser: messageGroup.isUser,
            isAttachment,
            thumbnailUrl,
            // Pass attachment info for icon rendering
            msgtype,
            filename,
            mimeType,
            size,
            caption,
          };
        }
      }
    }
    
    return {
      eventId: replyToEventId,
      content: '[Original message not found]',
      sender: 'Unknown',
      senderIsUser: false,
    };
  };

  const handleAvatarClick = () => {
    if (onContactClick) {
      onContactClick(senderId);
    }
  };

  const renderMessages = () => (
    <div className="message-group-wrapper">
      {
        messages.map((message, index) => {
          const isReply = isReplyMessage(message);
          const isEdited = message._isEdited || false;
          const originalReplyMessage = isReply ? getOriginalReplyMessage(message) : null;

          return (
            <MessageBubble
              key={message.getId()}
              message={message}
              name={name}
              isUser={isUser}
              shield={shield}
              isFirstInGroup={index === 0}
              isRead={(readReceipts.get(message.getId())?.size || 0) > 0}
              isLastInGroup={index === messages.length - 1}
              onDeleteMessage={onDeleteMessage}
              onReplyMessage={onReplyMessage}
              onEditMessage={onEditMessage}
              isEditHighlighted={editHighlightedMessageId === message.getId()}
              isSelected={selectedMessageId === message.getId()}
              isReply={isReply}
              isEdited={isEdited}
              originalReplyMessage={originalReplyMessage}
              onReactionDetailsOpen={onReactionDetailsOpen}
              onFocusMessage={onFocusMessage}
              onMessageDetailsOpen={onMessageDetailsOpen}
              onEditHistoryOpen={onEditHistoryOpen}
              onMentionClick={onContactClick}
              retryingTransactionIds={retryingTransactionIds}
              pendingTransactionIds={pendingTransactionIds}
              unsentTransactionIds={unsentTransactionIds}
            />
          );
        })
      }
    </div>
  );

  return (
    <div className={`message-group-container ${isUserClass}`}>
      <div className={`message-group ${isUserClass}`}>
        {
          (!isUser) && (
            <div className="message-group-avatar">
              <img
                src={avatar}
                alt={name}
                onClick={handleAvatarClick}
                crossOrigin="anonymous"
              />
            </div>
          )
        }
        {renderMessages()}
      </div>
    </div>
  );
};

MessageGroup.propTypes = {
  senderId: PropTypes.string,
  messages: PropTypes.array.isRequired,
  name: PropTypes.string.isRequired,
  avatar: PropTypes.string.isRequired,
  shield: PropTypes.object,
  isUser: PropTypes.bool.isRequired,
  onDeleteMessage: PropTypes.func,
  onReplyMessage: PropTypes.func,
  onEditMessage: PropTypes.func,
  editHighlightedMessageId: PropTypes.string,
  selectedMessageId: PropTypes.string,
  allMessageGroups: PropTypes.array,
  onReactionDetailsOpen: PropTypes.func,
  onMessageDetailsOpen: PropTypes.func,
  onFocusMessage: PropTypes.func,
  onContactClick: PropTypes.func,
  onEditHistoryOpen: PropTypes.func, // <-- Add this line
  retryingTransactionIds: PropTypes.instanceOf(Set),
  pendingTransactionIds: PropTypes.instanceOf(Set),
  unsentTransactionIds: PropTypes.instanceOf(Set),
};

export default MessageGroup;
